/**
 * blocks-cart-items.js
 * Standalone JS for the cart-items block.
 * Handles: quantity +/-, remove item, CSV download, recommendations toggle,
 *          complementary products variant toggle + checkout, clear cart modal.
 */
(function () {
  'use strict';

  var CART_CHANGE_URL = '/cart/change.js';
  var CART_ADD_URL = '/cart/add.js';

  /* --------------------------------------------------------------------------
     QUANTITY CONTROLS (buy-button pattern)
     -------------------------------------------------------------------------- */

  function initQuantityControls(container) {
    container.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-quantity-minus], [data-quantity-plus]');
      if (!btn) return;

      var wrapper = btn.closest('[data-quantity-selector]');
      if (!wrapper) return;

      var input = wrapper.querySelector('[data-quantity-input]');
      if (!input) return;

      var lineKey = wrapper.getAttribute('data-line-key');
      var max = parseInt(input.getAttribute('max'), 10) || Infinity;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var current = parseInt(input.value, 10) || 1;

      if (btn.hasAttribute('data-quantity-minus')) {
        current = Math.max(min, current - 1);
      } else if (btn.hasAttribute('data-quantity-plus')) {
        current = Math.min(max, current + 1);
      }

      input.value = current;

      var minusBtn = wrapper.querySelector('[data-quantity-minus]');
      var plusBtn = wrapper.querySelector('[data-quantity-plus]');
      if (minusBtn) minusBtn.disabled = current <= min;
      if (plusBtn) plusBtn.disabled = current >= max;

      if (lineKey) updateQuantity(lineKey, current, container);
    });

    container.addEventListener('change', function (e) {
      var input = e.target.closest('[data-quantity-input]');
      if (!input) return;

      var wrapper = input.closest('[data-quantity-selector]');
      if (!wrapper) return;

      var lineKey = wrapper.getAttribute('data-line-key');
      if (!lineKey) return;

      var max = parseInt(input.getAttribute('max'), 10) || Infinity;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var value = parseInt(input.value, 10);

      if (isNaN(value) || value < min) value = min;
      if (value > max) value = max;
      input.value = value;

      updateQuantity(lineKey, value, container);
    });
  }

  function updateQuantity(lineKey, quantity, container) {
    setLoading(container, true);
    fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    })
      .then(function (r) { return r.json(); })
      .then(function () { dispatchCartUpdated(); reloadCartSection(); })
      .catch(function (err) { console.error('Cart update failed:', err); setLoading(container, false); });
  }

  /* --------------------------------------------------------------------------
     REMOVE ITEM
     -------------------------------------------------------------------------- */

  function initRemoveButtons(container) {
    container.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-remove-key]');
      if (!btn) return;
      e.preventDefault();
      var lineKey = btn.getAttribute('data-remove-key');
      var row = btn.closest('tr[data-line-key]');

      if (row) { row.style.opacity = '0.3'; row.style.transition = 'opacity 0.2s ease'; }
      setLoading(container, true);

      fetch(CART_CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      })
        .then(function (r) { return r.json(); })
        .then(function () { dispatchCartUpdated(); reloadCartSection(); })
        .catch(function (err) { console.error('Cart remove failed:', err); if (row) row.style.opacity = '1'; setLoading(container, false); });
    });
  }

  /* --------------------------------------------------------------------------
     RECOMMENDATIONS TOGGLE
     -------------------------------------------------------------------------- */

  function initRecommendationsToggle(container) {
    var toggle = container.querySelector('[data-reco-toggle]');
    if (!toggle) return;

    var recoRows = container.querySelectorAll('[data-reco-container]');
    if (!recoRows.length) return;

    var saved = localStorage.getItem('cart-reco-toggle');
    if (saved === 'false') {
      toggle.checked = false;
      recoRows.forEach(function (r) { r.hidden = true; });
    } else {
      toggle.checked = true;
      recoRows.forEach(function (r) { r.hidden = false; });
    }

    toggle.addEventListener('change', function () {
      recoRows.forEach(function (r) { r.hidden = !toggle.checked; });
      localStorage.setItem('cart-reco-toggle', toggle.checked ? 'true' : 'false');
    });
  }

  /* --------------------------------------------------------------------------
     COMPLEMENTARY PRODUCTS — VARIANT TOGGLE ON CHECKBOX
     Reproduces the _buy-button.liquid behavior for comp-product checkboxes
     -------------------------------------------------------------------------- */

  function initCompProductVariants(container) {
    container.addEventListener('change', function (e) {
      if (!e.target.classList.contains('comp-product__checkbox')) return;

      var card = e.target.closest('[data-comp-product]');
      if (!card) return;

      var variantsPanel = card.querySelector('.comp-product__variants');
      if (!variantsPanel) return;

      if (e.target.checked) {
        variantsPanel.hidden = false;
      } else {
        variantsPanel.hidden = true;
      }
    });

    // Option pill/swatch click — update variant ID and price
    container.addEventListener('click', function (e) {
      var optionBtn = e.target.closest('[data-comp-option-value]');
      if (!optionBtn) return;

      var card = optionBtn.closest('[data-comp-product]');
      if (!card) return;

      var optionIndex = parseInt(optionBtn.getAttribute('data-comp-option-index'), 10);
      var optionValue = optionBtn.getAttribute('data-comp-option-value');

      // Deselect siblings
      var group = optionBtn.closest('.comp-product__option-group');
      if (group) {
        group.querySelectorAll('.is-selected').forEach(function (el) { el.classList.remove('is-selected'); });
      }
      optionBtn.classList.add('is-selected');

      // Find matching variant from JSON data
      var jsonEl = card.querySelector('[data-comp-variant-data]');
      if (!jsonEl) return;

      try {
        var data = JSON.parse(jsonEl.textContent);
        var selectedOptions = [];

        // Collect all selected options
        card.querySelectorAll('.comp-product__option-group').forEach(function (g) {
          var selected = g.querySelector('.is-selected');
          if (selected) {
            selectedOptions.push(selected.getAttribute('data-comp-option-value'));
          }
        });

        // Find variant matching all selected options
        var matchedVariant = null;
        for (var i = 0; i < data.variants.length; i++) {
          var v = data.variants[i];
          var match = true;
          for (var j = 0; j < selectedOptions.length; j++) {
            if (v.options[j] !== selectedOptions[j]) { match = false; break; }
          }
          if (match) { matchedVariant = v; break; }
        }

        if (matchedVariant) {
          card.setAttribute('data-variant-id', matchedVariant.id);

          // Update price
          var priceEl = card.querySelector('[data-comp-price]');
          if (priceEl) {
            var displayPrice = Math.round(matchedVariant.price * data.taxCoefficient);
            priceEl.textContent = '+' + formatMoney(displayPrice);
          }

          // Update compare price
          var compareEl = card.querySelector('[data-comp-compare]');
          if (compareEl && matchedVariant.compare_at_price > matchedVariant.price) {
            var displayCompare = Math.round(matchedVariant.compare_at_price * data.taxCoefficient);
            compareEl.textContent = formatMoney(displayCompare);
            compareEl.style.display = '';
          } else if (compareEl) {
            compareEl.style.display = 'none';
          }

          // Update image if variant has one
          if (matchedVariant.featured_image) {
            var imgEl = card.querySelector('.comp-product__image img');
            if (imgEl) imgEl.src = matchedVariant.featured_image;
          }

          // Mark unavailable options
          markUnavailableOptions(card, data, selectedOptions);
        }
      } catch (err) {
        console.error('Variant data parse error:', err);
      }
    });
  }

  function markUnavailableOptions(card, data, selectedOptions) {
    card.querySelectorAll('.comp-product__option-group').forEach(function (group, groupIdx) {
      group.querySelectorAll('[data-comp-option-value]').forEach(function (btn) {
        var testOptions = selectedOptions.slice();
        testOptions[groupIdx] = btn.getAttribute('data-comp-option-value');

        var available = data.variants.some(function (v) {
          var match = true;
          for (var i = 0; i < testOptions.length; i++) {
            if (testOptions[i] && v.options[i] !== testOptions[i]) { match = false; break; }
          }
          return match && v.available;
        });

        btn.classList.toggle('is-unavailable', !available);
      });
    });
  }

  function formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  /* --------------------------------------------------------------------------
     COMPLEMENTARY PRODUCTS — ADD ON CHECKOUT
     -------------------------------------------------------------------------- */

  function initComplementaryCheckout() {
    var checkoutBtn = document.querySelector('button[name="checkout"], [data-checkout-btn]');
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener('click', function (e) {
      var checked = document.querySelectorAll('.comp-product__checkbox:checked');
      if (!checked.length) return;

      e.preventDefault();
      checkoutBtn.setAttribute('aria-busy', 'true');

      var items = [];
      checked.forEach(function (cb) {
        var card = cb.closest('[data-comp-product]');
        if (!card) return;
        var variantId = card.getAttribute('data-variant-id');
        if (variantId) items.push({ id: parseInt(variantId, 10), quantity: 1 });
      });

      if (!items.length) {
        submitCheckout();
        return;
      }

      fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function () { submitCheckout(); })
        .catch(function () { submitCheckout(); });
    });
  }

  function submitCheckout() {
    window.location.href = '/checkout';
  }

  /* --------------------------------------------------------------------------
     CSV DOWNLOAD (icon button in header)
     -------------------------------------------------------------------------- */

  function initCSVDownload(container) {
    var btn = container.querySelector('[data-csv-download]');
    if (!btn) return;

    btn.addEventListener('click', function () {
      var rows = container.querySelectorAll('[data-csv-item]');
      if (!rows.length) return;

      var csvContent = 'Nom du produit,SKU de la variante,Quantité,Prix unitaire HT\n';
      rows.forEach(function (row) {
        var name = (row.getAttribute('data-csv-name') || '').replace(/"/g, '""');
        var sku = (row.getAttribute('data-csv-sku') || '').replace(/"/g, '""');
        var qty = row.getAttribute('data-csv-qty') || '0';
        var price = row.getAttribute('data-csv-price') || '0';
        csvContent += '"' + name + '","' + sku + '",' + qty + ',' + price + '\n';
      });

      var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      var link = document.createElement('a');
      var url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      var today = new Date();
      var dateStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
      link.setAttribute('download', 'panier_' + dateStr + '.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  }

  /* --------------------------------------------------------------------------
     CLEAR CART MODAL
     -------------------------------------------------------------------------- */

  function initClearCartModal(container) {
    var clearBtn = container.querySelector('[data-clear-cart]');
    var modal = container.querySelector('[data-clear-modal]');
    if (!clearBtn || !modal) return;

    var yesBtn = modal.querySelector('.main-cart__modal-yes');
    var noBtn = modal.querySelector('.main-cart__modal-no');

    clearBtn.addEventListener('click', function (e) {
      e.preventDefault();
      modal.hidden = false;
    });

    if (noBtn) {
      noBtn.addEventListener('click', function (e) {
        e.preventDefault();
        modal.hidden = true;
      });
    }

    if (yesBtn) {
      yesBtn.addEventListener('click', function (e) {
        e.preventDefault();
        window.location.href = '/cart/clear';
      });
    }

    // Close on overlay click
    modal.addEventListener('click', function (e) {
      if (e.target === modal) modal.hidden = true;
    });
  }

  /* --------------------------------------------------------------------------
     HELPERS
     -------------------------------------------------------------------------- */

  function setLoading(container, isLoading) {
    container.classList.toggle('main-cart__loading', isLoading);
  }

  function dispatchCartUpdated() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  function reloadCartSection() {
    window.location.reload();
  }

  /* --------------------------------------------------------------------------
     INIT
     -------------------------------------------------------------------------- */

  function init() {
    var container = document.querySelector('[data-cart-items-block]');
    if (!container) return;

    initQuantityControls(container);
    initRemoveButtons(container);
    initRecommendationsToggle(container);
    initCompProductVariants(container);
    initCSVDownload(container);
    initClearCartModal(container);
    initComplementaryCheckout();
  }

  /* --------------------------------------------------------------------------
     SAVE CART (PluralCart)
     -------------------------------------------------------------------------- */

  var SAVE_CART_FALLBACK = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'a/saved-carts';

  window.rodhouseSaveCart = function (btn) {
    if (btn && btn.getAttribute('aria-busy') === 'true') return;

    if (!window.cartSaver || typeof window.cartSaver.createCart !== 'function') {
      window.location.href = SAVE_CART_FALLBACK;
      return;
    }

    if (btn) {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    }

    fetch((window.Shopify.routes.root || '/') + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var payload = (cart.items || []).map(function (it) {
          return { quantity: it.quantity, variantId: it.variant_id };
        });
        if (!payload.length) {
          window.location.href = SAVE_CART_FALLBACK;
          return null;
        }
        return window.cartSaver.createCart(payload);
      })
      .then(function (response) {
        if (!response) return;
        if (response.status === 'success' && response.data && response.data.pubId) {
          window.location.href = (window.Shopify.routes.root || '/') + 'a/saved-carts/' + response.data.pubId;
        } else {
          window.location.href = SAVE_CART_FALLBACK;
        }
      })
      .catch(function (err) {
        console.error('Save cart error', err);
        if (btn) {
          btn.removeAttribute('aria-busy');
          btn.disabled = false;
        }
        window.location.href = SAVE_CART_FALLBACK;
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
